import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components/index";
import { withTranslation, WithTranslation } from "react-i18next";
import { logger } from "@lib/common";
import { UI } from "../../../../../core/ui";
import { RotateLoader } from "@lib/components";
import { Button } from "@lib/components";
import { BlockInfo } from "@lib/components";
import { Modal } from "@lib/components";
import { ModalContent, ModalTitle } from "@lib/components";
import { APIForm } from "./form";
import { MCPSection } from "./mcp-section";
import { ItemListWrapper, ItemListContent, ItemListItem } from "@lib/components";
import { FaTrashAlt, FaPencilAlt } from "react-icons/fa";
import { Tag } from "@lib/components";
import { LinkTag } from "@lib/components";
import { Input, FormGroup } from "@lib/components";
import QRCode from "qrcode.react";

interface Props extends WithTranslation { }
interface State {
	modal_active: boolean;
	modal_index: number;
	modal_for_mcp: boolean;
	delete_index: number | null;
	delete_loading: boolean;
	device_modal_active: boolean;
	device_registration_id: string | null;
	device_timer: number;
	device_items: T.Schema.Organisation.DeviceSchema[];
	device_loading: boolean;
	device_delete_index: number | null;
	device_delete_loading: boolean;
	device_select_restaurant_modal_active: boolean;
	device_selected_restaurant_id: string | null;
	device_register_method: "qr" | "pairing";
	device_pairing_code: string | null;
	device_nickname_index: number | null;
	device_nickname_value: string;
	device_nickname_loading: boolean;
}

@inject("store") @observer
class DashboardAPIClass extends MobxComponent<Props, State> {

	private device_timer_interval: number | null = null;

	constructor(props: Props) {
		super(props);
		this.state = {
			modal_active: false,
			modal_index: -1,
			modal_for_mcp: false,
			delete_index: null,
			delete_loading: false,
			device_modal_active: false,
			device_registration_id: null,
			device_timer: 60,
			device_items: [],
			device_loading: false,
			device_delete_index: null,
			device_delete_loading: false,
			device_select_restaurant_modal_active: false,
			device_selected_restaurant_id: null,
			device_register_method: "qr",
			device_pairing_code: null,
			device_nickname_index: null,
			device_nickname_value: "",
			device_nickname_loading: false,
		};
	}

	componentWillMount() {
		const { store } = this.injected;
		store.updateApis({ loading: true, error: "" });
	}

	componentDidMount() {
		this.get();
	}

	componentWillUnmount() {
		this.clear_device_timer();
	}

	componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
		if (this.state.modal_active && !prevState.modal_active) {
			const modal = document.getElementById("api-form-modal");
			if (modal) {
				setTimeout(() => {
					if (modal.scroll) {
						modal.scroll({ top: 0, left: 0, behavior: "auto" });
					}
				}, 100);
			}
		}

		const { store } = this.injected;
		const { last_registered_device_id } = store.notifications;
		const { device_registration_id, device_modal_active } = this.state;
		if (
			device_modal_active &&
			last_registered_device_id &&
			last_registered_device_id === device_registration_id
		) {
			this.close_device_modal();
			store.api.devices_find({}).then(res => {
				if (!res.outcome) this.setState({ device_items: res.items });
			}).catch(logger.captureException);
		}
	}

	get = async () => {
		const { store } = this.injected;
		store.updateApis({ loading: true, error: "", items: [] });
		try {
			const [response, response2, devicesResponse] = await Promise.all([
				store.api.api_find({}),
				store.api.restaurants(),
				store.api.devices_find({}),
			]);
			if (response.outcome || response2.outcome) {
				store.updateApis({
					loading: false,
					error: response.outcome ? response.message : "Something went wrong, try again or contact us",
					items: [],
				});
			}
			else {
				store.updateRestaurants({
					items: response2.items,
				});
				store.updateApis({
					loading: false,
					error: "",
					items: response.items,
				});
				if (!devicesResponse.outcome) {
					this.setState({ device_items: devicesResponse.items });
				}
			}
		}
		catch (e) {
			logger.captureException(e);
			store.updateApis({
				loading: false,
				error: "Something went wrong, try again or contact us",
				items: [],
			});
		}
	}

	delete_api = async (index: number | null) => {
		if (index === null) return;
		const { store } = this.injected;
		const { items } = store.apis;
		try {
			this.setState({ delete_loading: true });
			const res = await store.api.api_delete({ _id: items[index]._id });
			if (res.outcome) {
				UI.notification.error(res.message);
				this.setState({ delete_loading: false });
			}
			else {
				UI.notification.success("API deleted");
				const newItems = [...items];
				this.setState({
					delete_index: res.outcome ? index : null,
					delete_loading: false,
				});
				newItems.splice(index, 1);
				store.updateApis({ items: newItems });
			}
		}
		catch (e) {
			logger.captureException(e);
			this.setState({ delete_loading: false });
		}
	}

	clear_device_timer = () => {
		if (this.device_timer_interval !== null) {
			clearInterval(this.device_timer_interval);
			this.device_timer_interval = null;
		}
	}

	open_device_select_restaurant_modal = (device_register_method: State["device_register_method"]) => {
		this.setState({ device_select_restaurant_modal_active: true, device_selected_restaurant_id: null, device_register_method });
	}

	open_device_modal = async () => {
		const { store } = this.injected;
		const { device_selected_restaurant_id, device_register_method } = this.state;
		if (!device_selected_restaurant_id) return;
		this.setState({ device_loading: true, device_select_restaurant_modal_active: false });
		try {
			let next: Pick<State, "device_registration_id" | "device_pairing_code" | "device_timer">;
			if (device_register_method === "pairing") {
				const res = await store.api.devices_pairing_code({ restaurant_id: device_selected_restaurant_id });
				if (res.outcome) {
					UI.notification.error(res.message);
					this.setState({ device_loading: false });
					return;
				}
				next = {
					device_registration_id: res.device_registration_id,
					device_pairing_code: res.pairing_code,
					device_timer: Math.max(0, Math.round((new Date(res.pairing_expires_at).getTime() - Date.now()) / 1000)),
				};
			}
			else {
				const res = await store.api.devices_request({});
				if (res.outcome) {
					UI.notification.error(res.message);
					this.setState({ device_loading: false });
					return;
				}
				next = { device_registration_id: res.device_registration_id, device_pairing_code: null, device_timer: 60 };
			}
			this.setState({ ...next, device_loading: false, device_modal_active: true });
			this.device_timer_interval = window.setInterval(() => {
				this.setState(prev => {
					if (prev.device_timer <= 1) {
						this.clear_device_timer();
						return { device_timer: 0, device_modal_active: false, device_registration_id: null, device_pairing_code: null } as Pick<State, 'device_timer' | 'device_modal_active' | 'device_registration_id' | 'device_pairing_code'>;
					}
					return { device_timer: prev.device_timer - 1 } as Pick<State, 'device_timer'>;
				});
			}, 1000);
		}
		catch (e) {
			logger.captureException(e);
			this.setState({ device_loading: false });
		}
	}

	close_device_modal = () => {
		this.clear_device_timer();
		this.setState({ device_modal_active: false, device_registration_id: null, device_pairing_code: null, device_timer: 60, device_selected_restaurant_id: null });
	}

	delete_device = async (index: number | null) => {
		if (index === null) return;
		const { store } = this.injected;
		const { device_items } = this.state;
		const device = device_items[index];
		try {
			this.setState({ device_delete_loading: true });
			const res = await store.api.devices_delete({ device_registration_id: device.device_registration_id });
			if (res.outcome) {
				UI.notification.error(res.message);
				this.setState({ device_delete_loading: false });
			}
			else {
				UI.notification.success("Device removed");
				const newItems = [...device_items];
				newItems.splice(index, 1);
				this.setState({ device_items: newItems, device_delete_index: null, device_delete_loading: false });
			}
		}
		catch (e) {
			logger.captureException(e);
			this.setState({ device_delete_loading: false });
		}
	}

	save_device_nickname = async () => {
		const { store } = this.injected;
		const { device_nickname_index, device_nickname_value, device_items } = this.state;
		if (device_nickname_index === null) return;
		const device = device_items[device_nickname_index];
		this.setState({ device_nickname_loading: true });
		try {
			const res = await store.api.devices_nickname({
				device_registration_id: device.device_registration_id,
				device_nickname: device_nickname_value.trim(),
			});
			if (res.outcome) {
				UI.notification.error(res.message);
				this.setState({ device_nickname_loading: false });
			} else {
				const newItems = device_items.map((d, i) =>
					i === device_nickname_index ? { ...d, device_nickname: device_nickname_value.trim() } : d
				);
				this.setState({
					device_items: newItems,
					device_nickname_index: null,
					device_nickname_value: "",
					device_nickname_loading: false,
				});
			}
		} catch (e) {
			logger.captureException(e);
			this.setState({ device_nickname_loading: false });
		}
	}

	format_timer = (seconds: number) => {
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
	}

	loading = () => {
		return (
			<div className="m-t-12">
				<RotateLoader size={3} />
			</div>
		);
	}

	error = () => {
		const { store } = this.injected;
		const { error } = store.apis;
		return (
			<div className="m-t-12 text-center">
				<p className="big error-text m-b-4">{error}</p>
				<Button color="primary" size="sm" onClick={this.get}>
					Try Again
				</Button>
			</div>
		);
	}

	content = () => {
		const { store } = this.injected;
		const { items } = store.apis;
		const { restrictions } = store;
		const { device_items, device_loading } = this.state;
		return (
			<div>
			{(restrictions.api?.create || restrictions.api?.edit || restrictions.api?.delete) && <>
				<div className="flex-l-r-center m-b-7">
					<div>
						<h2 className="">API Keys</h2>
						{store.showMainSupport && <LinkTag href="https://apidocs.cloudwaitress.com" target="_blank" className="block m-t-2">View Documentation</LinkTag>}
					</div>
					{restrictions.api && restrictions.api.create && (
						<Button
							color="primary"
							round={true}
							size="xs"
							onClick={() => this.setState({ modal_active: true, modal_index: -1 })}>
							Create API Key
						</Button>
					)}
				</div>

				{items.length === 0 && (
					<div className="text-center m-t-12">
						<BlockInfo className="flex-center max200 center">
							No api keys found
						</BlockInfo>
					</div>
				)}

				{items.length !== 0 && (
					<ItemListWrapper>
						<ItemListContent>
							{items.map((s, i) => (
								<ItemListItem key={i} onClick={() => {
									if (restrictions.api && restrictions.api.edit) {
										this.setState({ modal_index: i, modal_active: true });
									}
								}}>
									<div className="p-lr-4 p-tb-2">
										<p className="m-b-1 big font-semi-bold">{s.name}</p>
										<p className="small">API Key <Tag onClick={(e) => e.stopPropagation()} className="cursor-text m-l-1 p-lr-2">{s.api_key}</Tag></p>
									</div>
									<div className="flex-center">
										<Button
											type="button"
											color="white"
											className="no-border"
											style={{ height: "62px" }}
											paddinglr={8}
											onClick={(e) => {
												if (restrictions.api && restrictions.api.delete) {
													e.stopPropagation();
													this.setState({ delete_index: i });
												}
											}}>
											{restrictions.api && restrictions.api.delete && <FaTrashAlt />}
										</Button>
									</div>
								</ItemListItem>
							))}
						</ItemListContent>
					</ItemListWrapper>
				)}
			</>}

			<MCPSection
				items={items}
				restaurants={store.restaurants.items}
				canCreate={!!restrictions.api?.create}
				onCreate={() => this.setState({ modal_active: true, modal_index: -1, modal_for_mcp: true })}
				fetchUsage={store.api.mcp_usage}
				fetchConnections={store.api.mcp_connections}
				revokeConnection={store.api.mcp_connection_revoke}
				updateConnectionRestaurants={store.api.mcp_connection_restaurants}
				fetchSeries={store.api.mcp_usage_series}
				fetchLimits={store.api.mcp_limits}
				updateLimits={store.api.mcp_limits_update}
				canEditLimits={store.auth.type === 'user'}
			/>

			{(restrictions.devices?.register || restrictions.devices?.delete) && <>

			<div className="flex-l-r-center m-b-7 m-t-10">
				<div>
					<h2 className="">Devices</h2>
				</div>
			<div className="flex-line centered">
			<Button
				color="primary-inverse"
				round={true}
				size="xs"
				className="m-r-2"
				disabled={device_loading || !restrictions.devices?.register}
				onClick={() => this.open_device_select_restaurant_modal("pairing")}>
				Register by Pairing Code
			</Button>
			<Button
				color="primary"
				round={true}
				size="xs"
				disabled={device_loading || !restrictions.devices?.register}
				onClick={() => this.open_device_select_restaurant_modal("qr")}>
				{device_loading ? <RotateLoader size={2} color="white" /> : "Register"}
			</Button>
			</div>
			</div>

			{device_items.length === 0 && (
				<div className="text-center m-t-12">
					<BlockInfo className="flex-center max200 center">
						No devices registered
					</BlockInfo>
				</div>
			)}

		{device_items.length !== 0 && (
			<ItemListWrapper>
				<ItemListContent>
					{device_items.map((d, i) => {
						const linkedRestaurant = store.restaurants.items.find(r => r._id === d.restaurant_id);
						const restaurantDomain = linkedRestaurant
							? (linkedRestaurant.domain
								? String(linkedRestaurant.domain)
								: `${linkedRestaurant.subdomain}.${store.reseller?.store_host || ""}`)
							: null;
						return (
							<ItemListItem key={i}>
								<div className="p-lr-4 p-tb-2" style={{ flex: 1 }}>
									{d.device_nickname && (
										<p className="m-b-1 big font-semi-bold">{d.device_nickname}</p>
									)}
									<p className={d.device_nickname ? "small" : "m-b-1 big font-semi-bold"}>{d.device_name}</p>
									<p className="small">Device ID <Tag className="cursor-text m-l-1 p-lr-2">{d.device_id}</Tag></p>
									{d.device_type && (
										<p className="small">Type <Tag className="cursor-text m-l-1 p-lr-2">{d.device_type}</Tag></p>
									)}
									{linkedRestaurant && (
										<p className="small m-t-1">{linkedRestaurant.name} &middot; <span className="text-muted">{restaurantDomain}</span></p>
									)}
									{!linkedRestaurant && d.restaurant_id && (
										<p className="small m-t-1 text-muted">Store no longer available</p>
									)}
								</div>
								<div className="flex-center">
									<Button
										type="button"
										color="white"
										className="no-border"
										style={{ height: "62px" }}
										paddinglr={8}
										onClick={(e) => {
											e.stopPropagation();
											this.setState({
												device_nickname_index: i,
												device_nickname_value: d.device_nickname || "",
											});
										}}>
										<FaPencilAlt />
									</Button>
									{restrictions.devices?.delete && (
									<Button
										type="button"
										color="white"
										className="no-border"
										style={{ height: "62px" }}
										paddinglr={8}
										onClick={(e) => {
											e.stopPropagation();
											this.setState({ device_delete_index: i });
										}}>
										<FaTrashAlt />
									</Button>
									)}
								</div>
							</ItemListItem>
						);
					})}
				</ItemListContent>
			</ItemListWrapper>
		)}

			</>}

			</div>
		);
	}

	close_api_modal = () => {
		this.setState({ modal_active: false, modal_index: -1, modal_for_mcp: false });
	}

	modal = () => {
		const { store } = this.injected;
		const { items } = store.apis;
		const { modal_active, modal_index, modal_for_mcp } = this.state;

		const type = modal_index === -1 ? "create" : "edit";

		let edit_id = "";
		let initialValues: T.Schema.API.APICESchema = {
			name: "",
			restaurants: [],
		};

		if (type === "edit") {
			const found = items[modal_index];
			edit_id = found._id;
			initialValues = {
				name: found.name,
				restaurants: found.restaurants,
			};
		}

		let title = type === "create" ? "Create New Key" : "Edit Key";
		if (type === "create" && modal_for_mcp) {
			title = "Create MCP Key";
		}

		return (
			<Modal
				id="api-form-modal"
				width={520}
				active={modal_active}
				alignTop={true}
				close={this.close_api_modal}>
				<ModalTitle className="round-top text-center">
					<h3>{title}</h3>
				</ModalTitle>
				<ModalContent>
					<APIForm
						edit_id={edit_id}
						restaurants={store.restaurants.items.map((r) => ({ value: r._id, label: r.name }))}
						initialValues={initialValues}
						back={this.close_api_modal}
					/>
				</ModalContent>
			</Modal>
		);

	}

	modal_delete = () => {
		const { delete_index, delete_loading } = this.state;
		const { store } = this.injected;
		const { restrictions } = store;
		const { items } = store.apis;

		let active = false;
		let item;
		if (delete_index !== null) {
			active = true;
			item = items[delete_index];
		}

		if (!restrictions.api || !restrictions.api.delete) {
			return null;
		}

		return (
			<Modal
				width="sm"
				close={() => this.setState({ delete_index: null })}
				active={active}>
				<ModalContent>
					<h4 className="m-b-1">Delete API Key</h4>
					<p className="underline">{item ? item.name : ""}</p>
				</ModalContent>
				<ModalContent>
					<p className="lhp big">Are you sure you want to delete this api key. Once deleted, it cannot be recovered</p>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button className="m-r-2 width100 max128" onClick={() => this.setState({ delete_index: null })}>Cancel</Button>
					<Button className="width100 max100px" color="primary" onClick={() => this.delete_api(delete_index)}>
						{delete_loading && <RotateLoader size={2} color="white" />}
						{!delete_loading && "Delete"}
					</Button>
				</ModalContent>
			</Modal>
		);
	}

	device_modal = () => {
		const { device_modal_active, device_registration_id, device_timer, device_selected_restaurant_id, device_register_method } = this.state;
		const qr_value = device_registration_id && device_selected_restaurant_id
			? `${device_registration_id}|${device_selected_restaurant_id}`
			: "";
		return (
			<Modal
				id="device-register-modal"
				width={400}
				active={device_modal_active && device_register_method === "qr"}
				alignTop={true}
				close={this.close_device_modal}>
				<ModalTitle className="round-top text-center">
					<h3>Scan to Register</h3>
				</ModalTitle>
				<ModalContent className="text-center">
					<p className="m-b-6">Use your device to scan the QR code below to register it.</p>
					{device_registration_id && (
						<div className="flex-center m-b-6">
							<QRCode value={qr_value} size={160} />
						</div>
					)}
					{/* TEMP: emulator helper, remove before release */}
					{qr_value && (
						<div className="m-b-6">
							<Input
								type="text"
								readOnly={true}
								value={qr_value}
								className="cursor-text"
								onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.currentTarget.select()}
							/>
							<Button
								size="xs"
								color="primary-inverse"
								className="m-t-2"
								onClick={() => {
									if (!navigator.clipboard) {
										UI.notification.error("Clipboard unavailable");
										return;
									}
									navigator.clipboard.writeText(qr_value).then(
										() => UI.notification.success("Copied"),
										() => UI.notification.error("Clipboard unavailable")
									);
								}}>
								Copy QR value
							</Button>
						</div>
					)}
					<Tag className="p-lr-4 p-tb-2 big">
						Expires in: <strong>{this.format_timer(device_timer)}</strong>
					</Tag>
				</ModalContent>
			</Modal>
		);
	}

	device_pairing_modal = () => {
		const { device_modal_active, device_pairing_code, device_timer, device_register_method } = this.state;
		return (
			<Modal
				id="device-pairing-modal"
				width={400}
				active={device_modal_active && device_register_method === "pairing"}
				alignTop={true}
				close={this.close_device_modal}>
				<ModalTitle className="round-top text-center">
					<h3>Pairing Code</h3>
				</ModalTitle>
				<ModalContent className="text-center">
					<p className="m-b-6">On the device, choose Login by Pairing Code and enter this code. It can only be used once.</p>
					{device_pairing_code && (
						<div className="m-b-6">
							<p className="font-semi-bold" style={{ fontSize: "32px", letterSpacing: "4px", fontFamily: "monospace" }}>
								{device_pairing_code}
							</p>
							<Button
								size="xs"
								color="primary-inverse"
								className="m-t-2"
								onClick={() => {
									if (!navigator.clipboard) {
										UI.notification.error("Clipboard unavailable");
										return;
									}
									navigator.clipboard.writeText(device_pairing_code).then(
										() => UI.notification.success("Copied"),
										() => UI.notification.error("Clipboard unavailable")
									);
								}}>
								Copy code
							</Button>
						</div>
					)}
					<Tag className="p-lr-4 p-tb-2 big">
						Expires in: <strong>{this.format_timer(device_timer)}</strong>
					</Tag>
				</ModalContent>
			</Modal>
		);
	}

	device_select_restaurant_modal = () => {
		const { store } = this.injected;
		const { device_select_restaurant_modal_active, device_selected_restaurant_id, device_loading } = this.state;
		const sortedRestaurants = [...store.restaurants.items].sort((a, b) =>
			a.name.localeCompare(b.name)
		);
		return (
			<Modal
				id="device-select-restaurant-modal"
				width={480}
				active={device_select_restaurant_modal_active}
				alignTop={true}
				close={() => this.setState({ device_select_restaurant_modal_active: false, device_selected_restaurant_id: null })}>
				<ModalTitle className="round-top text-center">
					<h3>Register Device</h3>
				</ModalTitle>
				<ModalContent>
					<FormGroup
						title="Select the store to link this device to"
						help="">
						<div className="flex-col" style={{ gap: "8px" }}>
							{sortedRestaurants.map(r => {
								const domain = r.domain
									? String(r.domain)
									: `${r.subdomain}.${store.reseller?.store_host || ""}`;
								const isSelected = device_selected_restaurant_id === r._id;
								return (
									<div
										key={r._id}
										className={`p-3 cursor-pointer border-radius${isSelected ? " bg-primary-light" : ""}`}
										style={{
											padding: "10px 14px",
											border: `2px solid ${isSelected ? "var(--color-primary, #1890ff)" : "#e0e0e0"}`,
											borderRadius: "6px",
											cursor: "pointer",
											background: isSelected ? "rgba(24,144,255,0.08)" : undefined,
										}}
										onClick={() => this.setState({ device_selected_restaurant_id: r._id })}>
										<p className="font-semi-bold" style={{ marginBottom: "2px" }}>{r.name}</p>
										<p className="small text-muted">{domain}</p>
									</div>
								);
							})}
						</div>
					</FormGroup>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button
						className="m-r-2 width100 max128"
						onClick={() => this.setState({ device_select_restaurant_modal_active: false, device_selected_restaurant_id: null })}>
						Cancel
					</Button>
					<Button
						className="width100 max128"
						color="primary"
						disabled={!device_selected_restaurant_id || device_loading}
						onClick={this.open_device_modal}>
						{device_loading ? <RotateLoader size={2} color="white" /> : "Continue"}
					</Button>
				</ModalContent>
			</Modal>
		);
	}

	device_nickname_modal = () => {
		const { device_nickname_index, device_nickname_value, device_nickname_loading } = this.state;
		const active = device_nickname_index !== null;
		return (
			<Modal
				width={400}
				active={active}
				close={() => this.setState({ device_nickname_index: null, device_nickname_value: "" })}>
				<ModalTitle className="round-top text-center">
					<h3>Set Device Nickname</h3>
				</ModalTitle>
				<ModalContent>
					<FormGroup title="Nickname" help="A friendly name to identify this device">
						<Input
							type="text"
							placeholder="e.g. Front Counter Tablet"
							value={device_nickname_value}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								this.setState({ device_nickname_value: e.target.value })
							}
						/>
					</FormGroup>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button
						className="m-r-2 width100 max128"
						onClick={() => this.setState({ device_nickname_index: null, device_nickname_value: "" })}>
						Cancel
					</Button>
					<Button
						className="width100 max100px"
						color="primary"
						onClick={this.save_device_nickname}>
						{device_nickname_loading ? <RotateLoader size={2} color="white" /> : "Save"}
					</Button>
				</ModalContent>
			</Modal>
		);
	}

	device_modal_delete = () => {
		const { store } = this.injected;
		const { restrictions } = store;
		if (!restrictions.devices || !restrictions.devices.delete) return null;
		const { device_delete_index, device_delete_loading, device_items } = this.state;
		const active = device_delete_index !== null;
		const item = device_delete_index !== null ? device_items[device_delete_index] : undefined;
		return (
			<Modal
				width="sm"
				close={() => this.setState({ device_delete_index: null })}
				active={active}>
				<ModalContent>
					<h4 className="m-b-1">Remove Device</h4>
					<p className="underline">{item ? item.device_name : ""}</p>
				</ModalContent>
				<ModalContent>
					<p className="lhp big">Are you sure you want to remove this device? It will need to be re-registered to access the system.</p>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button className="m-r-2 width100 max128" onClick={() => this.setState({ device_delete_index: null })}>No</Button>
					<Button className="width100 max100px" color="primary" onClick={() => this.delete_device(device_delete_index)}>
						{device_delete_loading && <RotateLoader size={2} color="white" />}
						{!device_delete_loading && "Yes"}
					</Button>
				</ModalContent>
			</Modal>
		);
	}

	render() {
		const { store } = this.injected;
		const { loading, error } = store.apis;
		return (
			<div>
				{loading && this.loading()}
				{(!loading && error) && this.error()}
				{(!loading && !error) && this.content()}
			{this.modal()}
			{this.modal_delete()}
			{this.device_select_restaurant_modal()}
			{this.device_modal()}
			{this.device_pairing_modal()}
			{this.device_modal_delete()}
			{this.device_nickname_modal()}
			</div>
		);
	}

}

export const DashboardAPI = withTranslation()(DashboardAPIClass);
